'use client';

import { useState, useRef } from 'react';
import { useTourStore } from '@eliadmin/core/src/stores/tours-store';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { IoClose } from 'react-icons/io5';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { TourSidebarFilterLabel } from '../tour-sidebar-filter-label';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';

export const TourSidebarFilterMaxPrice = () => {
  const { filter, setFilter } = useTourStore();
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { t } = useTranslations();
  const currency = useGlobalStore(state => state.generalLayout.currency);

  const handleMaxPriceChange = (value: string) => {
    if (value.trim() === '') {
      // Clear the value when input is empty
      setFilter({
        ...filter,
        price_to: undefined,
      });
      return;
    }

    const numericValue = Number(value);

    if (!isNaN(numericValue) && Number.isInteger(numericValue)) {
      // Update only if valid integer
      setFilter({
        ...filter,
        price_to: numericValue,
      });
    }
  };

  const clearMaxPrice = () => {
    setFilter({
      ...filter,
      price_to: undefined,
    });
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    // Allow only numbers, backspace, delete, and arrow keys
    if (
      !/[0-9]/.test(event.key) &&
      !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(event.key)
    ) {
      event.preventDefault();
    }
  };

  return (
    <div className="text-sm font-semibold">
      <TourSidebarFilterLabel label={t('text1857')} />
      <div className="relative">
        {/* Input Field */}
        <div
          className={cn(
            'flex h-12 min-w-[140px] items-center rounded-md border px-3',
            isFocused ? 'border-primary' : 'border-[#e5e7eb] bg-white',
          )}
        >
          <span className="mr-2 text-sm font-semibold text-primary">{currency}</span>
          <input
            ref={inputRef}
            type="text"
            inputMode="numeric"
            placeholder={t('9999')}
            defaultValue={filter.price_to?.toString() || ''}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            onChange={e => handleMaxPriceChange(e.target.value)}
            onKeyDown={handleKeyDown} // Restrict input to numeric keys
            className="flex-1 bg-transparent text-sm font-normal text-gray-800 outline-none"
          />
          {!!filter.price_to && (
            <IoClose
              onClick={clearMaxPrice}
              className="ml-2 h-4 w-4 cursor-pointer text-destructive-foreground"
            />
          )}
        </div>
      </div>
    </div>
  );
};
